import { ChevronDown, Lock } from 'lucide-react';
import { useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { isRouteActive } from '@/app/navigation/activeRoute';

import type { NavItem } from '@/app/navigation/navItems';
import { cn } from '@/shared/lib/cn';

/**
 * One row of the rail, and the expander when the row has children.
 *
 * <h2>Why a component rather than more JSX in `AppSidebar`</h2>
 *
 * A row with children needs state — whether it is open — and a component is the only place a
 * hook can live. Lifting that state into `AppSidebar` would mean a map of open ids there, so
 * the rail would hold the state of a row it does not otherwise know anything about.
 *
 * <h2>The measurements are the source's, not approximations of it</h2>
 *
 * Every length here is read off `the previous product's AppNav.tsx`
 * `renderSidebarItem` — `gap-3`, `py-2.5 pl-3` on the link, `pr-2` on the row, a 28px
 * (`h-7 w-7`) chevron and lock, a 20px icon, and `ml-5 … pl-3` on the submenu. The rail was
 * rebuilt to this design on 8 October 2026 from the design alone and came out visibly
 * looser; the second pass took the numbers.
 */

/**
 * The row's own box. Carries the active background, which is why it is on the WRAPPER and
 * not on the link: the chevron sits outside the link and a highlight that stopped at the
 * link's edge would leave an unpainted notch on the right of every expanded parent.
 */
const ROW =
  'group flex items-center gap-3 rounded-xl text-sm font-medium transition-colors duration-200';

const ROW_ACTIVE = 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300';

const ROW_IDLE = 'text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5';

export interface SidebarNavRowProps {
  item: NavItem;
  collapsed: boolean;
  /**
   * The children to draw, already filtered by what the person may see.
   *
   * <p>Passed in rather than read from `item.children` because the filtering needs the
   * session, which this component does not take — and a parent that rendered every child
   * would show somebody an agent they do not hold.
   */
  visibleChildren?: readonly NavItem[];
  /**
   * Whether to draw the padlock, for a product row the person has not been assigned.
   *
   * <p>The row is still a link and still navigates: what is behind it is `ProductRoute`,
   * which renders `ProductNotAssigned` rather than redirecting. The lock is the warning
   * that it will, not a control that stops it — and nothing here is a boundary, since every
   * endpoint behind the screen carries `@RequiresProduct` and refuses on its own.
   */
  locked?: boolean;
}

export function SidebarNavRow({
  item,
  collapsed,
  visibleChildren = [],
  locked = false,
}: SidebarNavRowProps) {
  const { pathname } = useLocation();
  const submenuId = useId();
  const Icon = item.icon;

  const hasChildren = visibleChildren.length > 0;
  const holdsActiveChild = visibleChildren.some((child) => isRouteActive(child.route, pathname));

  /*
   * Open if a child is the current page, which is what survives a refresh: the state is
   * seeded from the address rather than remembered, so a deep link to /agents/seo arrives
   * with the parent already open. After that it is the person's to toggle.
   */
  const [expanded, setExpanded] = useState(holdsActiveChild);
  const open = expanded || holdsActiveChild;

  /*
   * A parent whose child is current IS itself current, which is the source's rule and the
   * opposite of what this component did until 8 October 2026. Two painted rows for one page
   * is not the misreading it looks like: the parent is the group you are inside, and with the
   * submenu open the eye needs the group marked to see which group the child belongs to.
   * `aria-current` comes from `NavLink`, which carries `end` — so it stays on the EXACT
   * match and a screen reader is told about one current page, while the eye is shown the
   * group that page belongs to.
   *
   * <p>`isRouteActive` rather than equality, so a child route keeps its row lit —
   * `/email-warmup/create-account` is inside Email Warmup. That file has the design's rule.
   */
  const current = isRouteActive(item.route, pathname) || holdsActiveChild;

  const row = (
    <div className={cn(ROW, current ? ROW_ACTIVE : ROW_IDLE, collapsed ? 'p-0' : 'pr-2')}>
      <NavLink
        to={item.route}
        end
        title={collapsed ? item.label : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 rounded-xl focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
          collapsed ? 'justify-center p-3' : 'py-2.5 pl-3',
        )}
      >
        <Icon aria-hidden className="h-5 w-5 flex-shrink-0" />
        {collapsed ? null : <span className="truncate">{item.label}</span>}
      </NavLink>

      {/*
        The padlock, in its own 28px tile rather than beside the label. A tile the same size
        as the chevron keeps the right-hand edge of the rail straight whichever of the two a
        row carries -- and Email Warmup carries one while Developer Tools carries the other.
      */}
      {locked && !collapsed ? (
        <div
          aria-hidden
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-white/10"
        >
          <Lock className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
        </div>
      ) : null}

      {/*
        A separate control, not the row. The row navigates and this toggles, and one element
        cannot do both without the keyboard becoming ambiguous -- Enter on a link that also
        expands does the wrong one of the two.

        Hidden when collapsed: there is no room for children on a 70px rail, and the parent
        still navigates to its own screen.
      */}
      {hasChildren && !collapsed ? (
        <button
          type="button"
          onClick={() => {
            setExpanded((was) => !was);
          }}
          aria-expanded={open}
          aria-controls={submenuId}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label}`}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:text-gray-800 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ChevronDown
            aria-hidden
            className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      ) : null}
    </div>
  );

  if (!hasChildren || collapsed) {
    return row;
  }

  return (
    <div>
      {row}

      <ul
        id={submenuId}
        hidden={!open}
        // ml-5 puts the connector under the centre of the parent's 20px icon, and the border
        // is the hierarchy line -- one rule rather than a pseudo-element per child.
        className="mt-1 ml-5 space-y-0.5 border-l border-gray-200 pl-3 dark:border-white/10"
      >
        {visibleChildren.map((child) => {
          const ChildIcon = child.icon;

          return (
            <li key={child.id}>
              <NavLink
                to={child.route}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                    isActive
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'
                      : 'text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5',
                  )
                }
              >
                <ChildIcon aria-hidden className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{child.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
